import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import {
  FinancialAdjustmentService,
  PendingFinancialAdjustmentItem,
} from '../../bookings/services/financial-adjustment.service';

type FinancialAdjustmentApprovalRow = Omit<
  PendingFinancialAdjustmentItem,
  'tourPriceDelta' | 'expenseBudgetDelta' | 'customerChargeDelta' | 'companyAdvanceAmount' | 'booking'
> & {
  tourPriceDelta: number;
  expenseBudgetDelta: number;
  customerChargeDelta: number;
  companyAdvanceAmount: number;
  booking: Omit<NonNullable<PendingFinancialAdjustmentItem['booking']>, 'totalAmount' | 'estimatedExpense'> & {
    totalAmount: number;
    estimatedExpense: number;
  } | null;
};

@Component({
  selector: 'app-financial-adjustment-approval-page',
  imports: [
    DatePipe,
    DecimalPipe,
    RouterLink,
    LucideAngularModule,
    InputTextModule,
    TableModule,
    StatusBadge,
    RefreshButtonComponent,
  ],
  templateUrl: './financial-adjustment-approval-page.component.html',
})
export class FinancialAdjustmentApprovalPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly financialAdjustmentService = inject(FinancialAdjustmentService);

  readonly rows = signal<FinancialAdjustmentApprovalRow[]>([]);
  readonly isLoadingAdjustments = signal(false);
  readonly selectedAdjustment = signal<FinancialAdjustmentApprovalRow | null>(null);

  ngOnInit(): void {
    void this.loadAdjustments();
  }

  async loadAdjustments(): Promise<void> {
    if (this.isLoadingAdjustments()) return;
    this.isLoadingAdjustments.set(true);
    this.overlay.showLoading('กำลังโหลดคำขอปรับวงเงิน...');
    try {
      const adjustments = await this.financialAdjustmentService.getPending();
      this.rows.set(
        adjustments.map((adjustment) => ({
          ...adjustment,
          tourPriceDelta: Number(adjustment.tourPriceDelta),
          expenseBudgetDelta: Number(adjustment.expenseBudgetDelta),
          customerChargeDelta: Number(adjustment.customerChargeDelta),
          companyAdvanceAmount: Number(adjustment.companyAdvanceAmount),
          booking: adjustment.booking
            ? {
                ...adjustment.booking,
                totalAmount: Number(adjustment.booking.totalAmount),
                estimatedExpense: Number(adjustment.booking.estimatedExpense),
              }
            : null,
        })),
      );
    } catch (error: unknown) {
      this.overlay.error('โหลดคำขอปรับวงเงินไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.isLoadingAdjustments.set(false);
      this.overlay.hideLoading();
    }
  }

  openDetail(row: FinancialAdjustmentApprovalRow): void {
    this.selectedAdjustment.set(row);
  }

  closeDetail(): void {
    this.selectedAdjustment.set(null);
  }

  openAttachment(fileUrl: string | null): void {
    if (!fileUrl) return;
    window.open(
      this.financialAdjustmentService.resolveFileUrl(fileUrl),
      '_blank',
      'noopener,noreferrer',
    );
  }

  async approve(row: FinancialAdjustmentApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'อนุมัติคำขอปรับวงเงิน',
      message: `ยืนยันอนุมัติคำขอ ${row.adjustmentNo} หรือไม่`,
      confirmText: 'อนุมัติ',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังอนุมัติคำขอ...');
    try {
      await this.financialAdjustmentService.approve(row.id);
      this.rows.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeDetail();
      this.overlay.success('อนุมัติสำเร็จ', `${row.adjustmentNo} ได้รับการอนุมัติแล้ว`);
    } catch (error: unknown) {
      this.overlay.error('อนุมัติคำขอไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  async reject(row: FinancialAdjustmentApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ปฏิเสธคำขอปรับวงเงิน',
      message: `ยืนยันปฏิเสธคำขอ ${row.adjustmentNo} หรือไม่`,
      confirmText: 'ปฏิเสธคำขอ',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังปฏิเสธคำขอ...');
    try {
      await this.financialAdjustmentService.reject(row.id);
      this.rows.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeDetail();
      this.overlay.success('ปฏิเสธคำขอสำเร็จ', `${row.adjustmentNo} ถูกปฏิเสธแล้ว`);
    } catch (error: unknown) {
      this.overlay.error('ปฏิเสธคำขอไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private errorMessage(error: unknown): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : 'กรุณาลองใหม่อีกครั้ง';
  }
}
